import CommentListing from "components/CommentListing/CommentListing";
import React, { useEffect, useState } from "react";
import { PropertyToEdit } from "../../../../redux/reducers/Properties/PropertiesSliceTypes";
import { getReviews } from "../../../../redux/reducers/Properties/PropertiesSlice";
import { AppDispatch } from "redux/store";
import { useDispatch } from "react-redux";
import { Star, MessageSquare, Sparkles, Award } from "lucide-react";

interface ReviewProps {
  propertyData: PropertyToEdit | null;
}

const Review: React.FC<ReviewProps> = ({ propertyData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showReview, setShowReview] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const review = propertyData?.average?.average_review;
  const formattedReview = review ? review.toFixed(1) : null;
  const reviewCount = reviews?.length || 0;

  const getReviewData = async () => {
    if (!propertyData) {
      return;
    }
    const propertyId = propertyData.id;
    try {
      setShowReview(true);
      const result = await dispatch(getReviews(propertyId)).unwrap();
      setReviews(result?.reviews || []);
    } catch (error: any) {
    } finally {
      setShowReview(false);
    }
  };

  useEffect(() => {
    getReviewData();
  }, []);

  const reviewCategories = [
    {
      label: "Accuracy",
      rating: propertyData?.average?.accuracy || 0,
      icon: "📍",
    },
    {
      label: "Communication",
      rating: propertyData?.average?.communication || 0,
      icon: "💬",
    },
    {
      label: "Cleanliness",
      rating: propertyData?.average?.cleanliness || 0,
      icon: "✨",
    },
    {
      label: "Location",
      rating: propertyData?.average?.location || 0,
      icon: "📍",
    },
    {
      label: "Check In",
      rating: propertyData?.average?.check_in || 0,
      icon: "🔑",
    },
    {
      label: "Value",
      rating: propertyData?.average?.values || 0,
      icon: "💎",
    },
  ];

  // Sample review for empty state
  const sampleReview = {
    id: "sample-1",
    user: {
      name: "Sample Guest",
      avatar: null,
    },
    rating: 5,
    comment: "This property looks amazing! Be the first to leave a review and share your experience with future guests.",
    date: new Date().toISOString(),
    is_sample: true,
  };

  const displayReviews = reviewCount > 0 ? reviews : [sampleReview];

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl">
              <Star size={24} className="text-yellow-500 fill-yellow-500" />
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Guest reviews
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {reviewCount > 0 
                  ? `${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'} from verified guests`
                  : 'No reviews yet'}
              </p>
            </div>
          </div>

          {/* Overall Rating Badge */}
          {formattedReview && (propertyData?.average?.count ?? 0) > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
              <Star size={20} className="text-yellow-500 fill-yellow-500" />
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {formattedReview}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                / 5.0
              </span>
            </div>
          )}
        </div>

        <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent" />

        {/* Rating Categories Grid */}
        {(propertyData?.average?.count ?? 0) > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {reviewCategories.map((category, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {category.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star size={16} className="text-yellow-500 fill-yellow-500" />
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {category.rating.toFixed(1)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {displayReviews.map((review) => (
          <div key={review.id}>
            {review.is_sample ? (
              /* Sample Review Card */
              <div className="relative p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border-2 border-dashed border-blue-300 dark:border-blue-700">
                <div className="absolute top-3 right-3">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                    <Sparkles size={12} />
                    Sample
                  </span>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center text-white font-semibold text-lg">
                      ?
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {review.user.name}
                      </h4>
                      <div className="flex items-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            className="text-yellow-500 fill-yellow-500"
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                      {review.comment}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-700 dark:text-blue-300 flex items-center gap-2">
                    <MessageSquare size={14} />
                    Be the first to write a review after your stay!
                  </p>
                </div>
              </div>
            ) : (
              /* Real Review */
              <CommentListing data={review} />
            )}
          </div>
        ))}
      </div>

      {/* Empty State with CTA */}
      {reviewCount === 0 && (
        <div className="mt-6 p-6 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700">
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="p-3 bg-[#f5694b]/10 rounded-full">
              <Award size={32} className="text-[#f5694b]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                No reviews yet
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md">
                This property is waiting for its first review. Book your stay and be 
                the first to share your experience with the community!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Info */}
      {reviewCount > 0 && (
        <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
          <p className="text-sm text-yellow-900 dark:text-yellow-100">
            <span className="font-semibold">Verified reviews:</span> All reviews are from 
            guests who have completed a stay at this property.
          </p>
        </div>
      )}
    </div>
  );
};

export default Review;